import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError, toApiError } from '@/lib/api'
import type { ConversationResponse } from '@/lib/api'
import { useAnswerApi } from '@/hooks/useAnswerApi'

/** No pagination server-side (VC-729 scope) — render at most this many. */
const MAX_LISTED = 50

export type ConversationsState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; conversations: ConversationResponse[] }
  | { kind: 'error'; message: string }

export type UseConversationsOptions = {
  baseUrl: string
  acquireToken: () => Promise<string>
  fetchImpl?: typeof fetch
  /** Gate the initial load on this — e.g. `auth.kind === 'authed'`. Loading
   * while unauthenticated would record a token error as a list failure. */
  enabled?: boolean
}

export type UseConversationsResult = {
  state: ConversationsState
  refresh: () => Promise<void>
  rename: (id: string, title: string) => Promise<void>
  remove: (id: string) => Promise<void>
}

export function useConversations(options: UseConversationsOptions): UseConversationsResult {
  const enabled = options.enabled ?? true
  const [state, setState] = useState<ConversationsState>({ kind: 'idle' })
  const abortRef = useRef<AbortController | null>(null)

  const api = useAnswerApi(options)

  // Silent refresh: keep showing the current list until the new one arrives;
  // only surface an error when there is no list to fall back on.
  const refresh = useCallback(async () => {
    const controller = new AbortController()
    abortRef.current?.abort()
    abortRef.current = controller
    try {
      const conversations = await api.listConversations({ signal: controller.signal })
      if (controller.signal.aborted) return
      setState({ kind: 'ready', conversations: conversations.slice(0, MAX_LISTED) })
    } catch (err) {
      if (controller.signal.aborted) return
      const apiErr: ApiError = err instanceof ApiError ? err : await toApiError(err)
      setState((prev) =>
        prev.kind === 'ready' ? prev : { kind: 'error', message: apiErr.message },
      )
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
      }
    }
  }, [api])

  const rename = useCallback(
    async (id: string, title: string) => {
      const trimmed = title.trim()
      if (trimmed === '') return
      try {
        await api.renameConversation({
          conversationId: id,
          conversationUpdateRequest: { title: trimmed },
        })
      } catch {
        // 404/403 here means our list is stale; the refresh below reconciles.
      }
      await refresh()
    },
    [api, refresh],
  )

  const remove = useCallback(
    async (id: string) => {
      try {
        await api.deleteConversation({ conversationId: id })
      } catch {
        // 404 here means it was already gone; the refresh below reconciles.
      }
      await refresh()
    },
    [api, refresh],
  )

  // Load when enabled flips true (typically once auth resolves). The effect
  // reads `refresh` through a ref so consumers passing inline
  // acquireToken/fetchImpl closures (new identity every render) don't
  // re-trigger it in a render → fetch → setState loop.
  const refreshRef = useRef(refresh)
  useEffect(() => {
    refreshRef.current = refresh
  })
  useEffect(() => {
    if (!enabled) return
    setState((prev) => (prev.kind === 'idle' ? { kind: 'loading' } : prev))
    void refreshRef.current()
    return () => {
      abortRef.current?.abort()
    }
  }, [enabled])

  return { state, refresh, rename, remove }
}
